// The delta-v map's rail panel: the face `src/sim/dvMap.ts` puts on screen
// while flying. Narrow and short on purpose: the picked trip, its flight plan
// folded behind FLIGHT PLAN, GO with CANCEL and WARP TO NEXT, the autopilot's
// own line while it flies, and a short list to pick from. Every figure for every
// trip is in the wide window its ALL TRIPS button opens (src/hudDvTable.ts).
//
// Beside src/hudDrag.ts and src/hudMinimize.ts rather than inside src/hud.ts,
// which paints the combat HUD's own per-frame frame; this panel redraws on a
// change of map state and not on a tick. The markup is an ordinary
// `[data-widget]` panel with a `.hud-head`, so the rail's reorder, dock, fold
// and minimize behaviour reaches it with no case of its own.
//
// Every term on the panel is a plain word, and its explanation hangs off the
// row itself as the HUD's styled tooltip (`.hud-tip` + `data-tooltip`,
// src/hudTip.ts). No badge, no pill and no glyph: that affordance took a
// column's width, changed the row's height and read as a button. The one
// exception is the destination list: its rows carry no tooltip (it opened over
// the five rows below), and a bare (?) beside the heading says it once.

import { track } from './visits/bus';
import { tripPlanned } from './visits/params';
import { createDvTable } from './hudDvTable';
import {
  FREE_TIP, NOTE_TIP, NOTHING, NO_GO_TIP, MOVED_TIP, PHASE_WORDS, PLANE_TIP, RELATIVE_TIP,
  TEXTBOOK_TIP, WHEN_TIP, burnFigure, burnTip, days, durationText, dvText, goOffered, kms,
  movedRowText, paintPlan, plainBodyName, planRows, planTip, tip, tripClockText, waitText,
} from './hudDvText';
import type { PlanCol } from './hudDvText';
import { arrivalAltitudeKm, escapeDvKmS, highOrbitOf } from './sim/arrivalOrbit';
import type { ArrivalOrbit } from './sim/arrivalOrbit';
import type { AutopilotPhase, AutopilotStep } from './sim/autopilot';
import { correctionRowText, departureRowText } from './sim/dvCorrection';
import type { DvCorrectionState } from './sim/dvCorrection';
import type { DvMap, DvMapState } from './sim/dvMap';
import { finiteEscapeDvKmS, gmKm3S2 } from './sim/transfer';
import type { TransferPlan } from './sim/transfer';
import type { BodyRow } from './world/types';
import { TEST_SWITCHES } from './urlFlags.ts';

export { goOffered, movedRowText };

/** Where the ARRIVE IN pick is kept between visits, the way the NAMES switch keeps its own. */
const ORBIT_KEY = 'hud-go-arrival';

/** The remembered ARRIVE IN pick; HIGH where none is kept. */
export function loadArrivalOrbit(): ArrivalOrbit {
  try {
    return localStorage.getItem(ORBIT_KEY) === 'low' ? 'low' : 'high';
  } catch {
    return 'high';
  }
}

function saveArrivalOrbit(orbit: ArrivalOrbit): void {
  try {
    localStorage.setItem(ORBIT_KEY, orbit);
  } catch {
    // Private mode or a full quota: the pick still holds for this visit.
  }
}

/** The prograde climb out of a circle of `radiusKm` at the ship's thrust, as the map prices departures; the impulse figure where the ship has none. */
function leaveKmS(body: BodyRow, radiusKm: number, accelKmS2: number): number {
  const gm = gmKm3S2(body);
  return finiteEscapeDvKmS(gm, { x: radiusKm, y: 0, z: 0 }, { x: 0, y: Math.sqrt(gm / radiusKm), z: 0 }, accelKmS2, 0)
    ?? escapeDvKmS(body, radiusKm);
}

const groupKm = (km: number): string => Math.round(km).toLocaleString('en-US');

/** ARRIVE IN's tooltip at one destination: both orbits with their speed and the push to leave each, and what held HIGH where it is. */
function orbitTip(body: BodyRow, rows: readonly BodyRow[], accelKmS2: number): string {
  const name = plainBodyName(body.display_name);
  const radiusKm = body.radius_m / 1000;
  const lowKm = arrivalAltitudeKm(body, rows, 'low');
  const highKm = arrivalAltitudeKm(body, rows, 'high');
  const speed = (km: number): string => Math.sqrt(gmKm3S2(body) / (radiusKm + km)).toFixed(1);
  const leave = (km: number): string => leaveKmS(body, radiusKm + km, accelKmS2).toFixed(1);
  const high = highOrbitOf(body, rows);
  const moon = high.moonName === null ? null : plainBodyName(high.moonName);
  const why = high.limit === 'low'
    ? ` Here HIGH is the same orbit as LOW: ${moon === null ? `the whole of ${name} is in view from it already` : `no higher orbit keeps clear of ${moon}`}.`
    : high.squeezed ? ` HIGH is the highest orbit that keeps clear of ${moon ?? 'its moons'}, though ${name}'s rings reach past it.`
      : high.limit === 'moon' ? ` HIGH sits outside ${moon}'s path, clear of it.`
        : high.limit === 'rings' ? ` HIGH stays outside ${name}'s rings.`
          : high.limit === 'sphere' ? ` HIGH stays well inside the reach of ${name}'s own pull.` : '';
  return `The orbit GO brakes you into at ${name}. LOW: ${groupKm(lowKm)} km over ${name} at ${speed(lowKm)} km/s, `
    + `fast and close with ${name} filling the view; ${leave(lowKm)} km/s to leave it again. `
    + `HIGH: ${groupKm(highKm)} km up at ${speed(highKm)} km/s, slower, with the whole of ${name} in view and space round it; `
    + `${leave(highKm)} km/s to leave it again.${why} Beside the buttons, the push it takes to leave the orbit picked. `
    + 'Your pick is kept for the next trip.';
}

/**
 * One row of the glossary: the id of a row in the static markup, the term it
 * carries, and the text its tooltip says. A copy of a row elsewhere carries the
 * id as `data-glossary` and gets the same text.
 */
export const DV_MAP_GLOSSARY: ReadonlyArray<{ rowId: string; label: string; tip: string }> = [
  {
    rowId: 'dv-table-toggle',
    label: 'ALL TRIPS',
    tip: 'Opens the wide window: the flight plan, then every trip with both pushes, dates and the flat-orbit and moon-plane figures. Escape closes it.',
  },
  {
    rowId: 'dv-origin-row',
    label: 'PLAN FROM',
    tip: 'WHERE I AM: plan from the ship\'s current orbit. PARKING ORBIT: plan from a 400 km circular orbit, the figure the written costs use.',
  },
  {
    rowId: 'dv-from-row',
    label: 'LEAVING FROM',
    tip: 'The world the trip leaves and your height over it. Deep space means no world pulls hard enough to leave from.',
  },
  {
    rowId: 'dv-when-row',
    label: 'DATE',
    tip: 'The day the plan was worked out, on the bridge clock.',
  },
  {
    rowId: 'dv-col-dest',
    label: 'WHERE TO',
    tip: 'Places this map can plan a trip to, cheapest first. Click a row to pick that trip for GO.',
  },
  {
    rowId: 'dv-row-help',
    label: '(?)',
    tip: 'Each row is a trip, cheapest first, with its cost and wait. Click one to pick it for GO. A dimmed row is still being priced.',
  },
  {
    rowId: 'dv-col-total',
    label: 'SPEED CHANGE',
    tip: 'What the whole trip costs you if you leave on the best day, from your orbit now to the orbit ARRIVE IN picks. Compare destinations by this.',
  },
  {
    rowId: 'dv-col-tof',
    label: 'TRAVEL TIME',
    tip: 'Days from leaving to arriving. The wait for the window is separate.',
  },
  {
    rowId: 'dv-col-window',
    label: 'LEAVES IN',
    tip: 'How long you wait for the cheapest window. Leaving early costs more than the SPEED CHANGE.',
  },
  {
    rowId: 'dv-col-leave',
    label: 'LEAVE',
    tip: 'The first push: its speed change and engine time at GO\'s thrust. Under it, which way to push and the instant-push cost.',
  },
  {
    rowId: 'dv-col-arrive',
    label: 'ARRIVE',
    tip: 'The last push: its speed change and engine time. Under it, orbit or a stop beside a small body, and which way to push.',
  },
  { rowId: 'dv-col-past', label: 'SPEED PAST ON ARRIVAL', tip: RELATIVE_TIP },
  { rowId: 'dv-col-free', label: 'IN OPEN SPACE', tip: FREE_TIP },
  {
    rowId: 'dv-col-textbook',
    label: 'FLAT CIRCLES',
    tip: `${TEXTBOOK_TIP} Under it, that trip's time.`,
  },
  { rowId: 'dv-col-dates', label: 'LEAVES / ARRIVES', tip: WHEN_TIP },
  { rowId: 'dv-col-plane', label: 'MOON PLANE', tip: PLANE_TIP },
  {
    rowId: 'dv-pick-name-row',
    label: 'PICKED TRIP',
    tip: 'The trip chosen from the list, with its SPEED CHANGE. GO flies this one.',
  },
  {
    rowId: 'dv-orbit-row',
    label: 'ARRIVE IN',
    tip: 'The orbit GO brakes you into at the far end. LOW: fast and close to the surface, the world filling the view. HIGH: slower, the whole world in view with space round it, and cheaper to leave again. Beside the buttons, the push it takes to leave the orbit picked. Your pick is kept for the next trip.',
  },
  {
    rowId: 'dv-pick-leave-row',
    label: 'LEAVE',
    tip: 'The first push of the picked trip: its speed change and how long the engine is open at GO\'s thrust.',
  },
  {
    rowId: 'dv-pick-arrive-row',
    label: 'ARRIVE',
    tip: 'The last push of the picked trip: its speed change and engine time. It brakes you into orbit, or to a stop beside a small body.',
  },
  {
    rowId: 'dv-go-row',
    label: 'GO',
    tip: 'GO hands the ship to the autopilot, which flies the picked trip. CANCEL, or touching the stick, throttle or flight assist, takes it back.',
  },
  {
    rowId: 'dv-autowarp',
    label: 'AUTO-WARP',
    tip: 'GO runs the clock for the whole trip and slows for each burn. Pressing a warp button takes the clock back.',
  },
  {
    rowId: 'dv-fly-trip-row',
    label: 'TRIP TIME',
    tip: 'Game time since you pressed GO, beside the planned trip length.',
  },
  {
    rowId: 'dv-depart-row',
    label: 'DEPARTURE',
    tip: 'When to start the burn that leaves this orbit. Starting early or late costs extra fuel.',
  },
  {
    rowId: 'dv-fix-row',
    label: 'COURSE FIX',
    tip: 'The burn back onto the planned arrival: point at the COURSE FIX marker and push until the figure is gone. Finish before the capture burn.',
  },
  {
    rowId: 'dv-warp-skip',
    label: 'WARP TO NEXT',
    tip: 'Skips the clock to the next autopilot step, stopping a day short if it is over two days off, else 10 minutes short.',
  },
  {
    rowId: 'dv-plan-toggle',
    label: 'FLIGHT PLAN',
    tip: 'Every step GO flies, in order, with when each starts and what it spends. Press to show or hide the list.',
  },
  {
    rowId: 'dv-table-plan-head',
    label: 'FLIGHT PLAN',
    tip: 'Every step GO flies, timed from when the plan was worked out. A course fix is priced only on the day, if the ship has drifted.',
  },
  { rowId: 'dv-plan-col-step', label: 'STEP', tip: 'What the autopilot does at that point.' },
  {
    rowId: 'dv-plan-col-at',
    label: 'STARTS IN',
    tip: 'Time from when the plan was worked out until the step starts.',
  },
  {
    rowId: 'dv-plan-col-dv',
    label: 'SPEED CHANGE',
    tip: 'What the step spends. The two pushes add up to the trip\'s SPEED CHANGE; a course fix spends only what the drift needs.',
  },
  {
    rowId: 'dv-plan-col-lasts',
    label: 'LASTS',
    tip: 'How long the step takes: a push at GO\'s thrust, or a wait or coast until the next step.',
  },
  {
    rowId: 'dv-plan-col-how',
    label: 'HOW',
    tip: 'Which way each push points, and where the trip leaves you.',
  },
  {
    rowId: 'dv-table-trip-head',
    label: 'TRIP UNDER WAY',
    tip: 'The trip that has left: the autopilot\'s report and what it spent, or, flown by hand, the COURSE FIX back onto the arrival.',
  },
  {
    rowId: 'dv-fly-dest-row',
    label: 'FLYING TO',
    tip: 'The world the autopilot is flying to: the trip picked when you pressed GO.',
  },
  {
    rowId: 'dv-fly-phase-row',
    label: 'DOING NOW',
    tip: 'The autopilot\'s current leg: waiting, leaving, coasting, correcting or braking.',
  },
  {
    rowId: 'dv-fly-next-step-row',
    label: 'NEXT STEP',
    tip: 'The step WARP TO NEXT jumps to: the window, a course fix or the arrival. Shown only while it is offered.',
  },
  {
    rowId: 'dv-fly-dv-row',
    label: 'PUSH LEFT',
    tip: 'Speed change the current push still owes before the engine shuts down. Blank while coasting.',
  },
  {
    rowId: 'dv-fly-next-row',
    label: 'NEXT STEP IN',
    tip: 'Bridge-clock time until the next autopilot step. Inside a push, the time until the engine shuts down.',
  },
  {
    rowId: 'dv-fly-trim-row',
    label: 'COURSE FIXES',
    tip: 'Speed change the course fixes have spent so far. The trip\'s SPEED CHANGE does not price these.',
  },
  {
    rowId: 'dv-fly-spent-row',
    label: 'SPENT',
    tip: 'Speed change every push has spent so far, beside the trip\'s planned total. Spent is larger when the plan priced a push as instant.',
  },
];

// The autopilot's own line this frame for `DvMapPanel.setFlight`; null there means
// nothing is flying and the panel draws GO instead.
export interface DvMapFlight {
  readonly phase: AutopilotPhase;
  /** The destination the autopilot took, which is not `state.selectedId`: the
   *  player may pick another row while it flies. */
  readonly destinationName: string;
  readonly dvToGoMps: number;
  readonly nextEventS: number;
  readonly trimSpentMps: number;
  /** What every push has spent so far and what the map priced the trip at, both
   *  off the autopilot's own command: the panel holds no copy. */
  readonly spentMps: number;
  readonly pricedDvMps: number;
  /** Why it let go, once it has. Null while it still has the stick. */
  readonly reason: string | null;
  /** It has let go: the lines are its last report and GO is offered again. */
  readonly ended: boolean;
  /** The dated step the skip button would take the clock to, or null where the
   *  autopilot is inside a push and nothing is being waited out. */
  readonly skipStep: AutopilotStep | null;
  /** `skipStep` in the flight plan's own words, a course fix numbered the way
   *  the FLIGHT PLAN list numbers it. Empty where `skipStep` is null. */
  readonly nextStepLabel: string;
  /** Simulated seconds the skip would take off the wait. 0 hides the button:
   *  the step is already inside the near lead a second press stops short by. */
  readonly skipSeconds: number;
  /** A skip is running its ticks right now. */
  readonly skipping: boolean;
  /** Auto-warp on this trip: pacing the clock, stood down by a warp pick or a
   *  load until the trip ends, or switched off. */
  readonly autoWarp: 'driving' | 'stood down' | 'off';
  /** Game seconds since GO, and from GO to the plan's arrival; null once it has let go. */
  readonly tripElapsedS: number | null;
  readonly tripPlannedS: number | null;
}

/** What the two buttons do. The panel owns neither the autopilot nor the plan. */
export interface DvMapFlightControl {
  /** Fly the picked row. */
  go: () => void;
  /** Hand the stick back. */
  cancel: () => void;
  /** Take the clock to a day short of the autopilot's next step, or to ten
   *  simulated minutes short of it on a second press once the day is reached. */
  skip: () => void;
  /** The AUTO-WARP switch: GO paces the clock itself, remembered between visits. */
  autoWarp: { on: () => boolean; set: (on: boolean) => void };
}

/**
 * The control (`?dvmapnotip=1`): one row of the static glossary left with no
 * tooltip text at all. A reading that only asks whether the tooltips it FINDS
 * carry text cannot see a row carrying none, which is how a pilot-panel row
 * shipped with no explanation at all; this is the state that reading has to
 * catch.
 */
const DROP_TIP_ROW = TEST_SWITCHES && new URLSearchParams(location.search).get('dvmapnotip') === '1'
  ? 'dv-from-row'
  : null;

/**
 * The control (`?dvmaprowtips=1`): every destination row back to carrying its
 * own long tooltip, which opens over the rows under it. The destination-row
 * leg in tools/preview/dojo/arms/dvmap.mjs has to catch it.
 */
const ROW_TIPS = TEST_SWITCHES && new URLSearchParams(location.search).get('dvmaprowtips') === '1';

/**
 * The control (`?dvmaprowreplace=1`): put the destination list back to
 * throwing every row away and rebuilding the lot on each chunk of a survey
 * fill, instead of `paintRowList`'s in-place diff. A click already
 * travelling toward a row can then land on a node the very next chunk has
 * already replaced - the row-survives-a-fill leg in tools/preview/dojo/arms/
 * dvmap.mjs has to catch it.
 */
const ROW_REPLACE = TEST_SWITCHES && new URLSearchParams(location.search).get('dvmaprowreplace') === '1';

/**
 * The control (`?dvmaptall=1`): the rail panel back at its old size - the
 * rail's full width, the long list cap, the list, the picked trip and the
 * COURSE FIX row kept while GO flies, and GO itself no reason to keep the
 * panel out of the height fold. tools/preview/dvmap-panel-fit.mjs has to
 * catch it running off the window at 800x500.
 */
const TALL = TEST_SWITCHES && new URLSearchParams(location.search).get('dvmaptall') === '1';

/**
 * The control (`?dvmapnogutter=1`): the trip list's gutter and its reserved
 * scroll-bar track taken off again, so the LEAVES IN text runs up against the
 * bar. tools/preview/dvmap-panel-fit.mjs's gutter leg has to catch it.
 */
const NO_GUTTER = TEST_SWITCHES && new URLSearchParams(location.search).get('dvmapnogutter') === '1';

/** The rail's flight plan: the step, when it starts and what it spends. */
const RAIL_PLAN_COLS: readonly PlanCol[] = ['step', 'at', 'dv'];

interface DvMapPanelConfig {
  map: DvMap;
  /** The panel element, `[data-widget="dv-map"]`. */
  root: HTMLElement;
  /** Go and Cancel. Absent leaves the row off the panel entirely. */
  flight?: DvMapFlightControl;
  /** The ship's own sustained acceleration, m/s2, for the burn durations
   *  beside each push. Read per paint, so it follows the live config rather
   *  than a number copied at boot. Without it the pushes show no duration. */
  accelMps2?: () => number;
  /** Share of full thrust GO flies a trip's burns at under the radiators; the
   *  durations are priced at it. 1 where absent. */
  thrustShare?: () => number;
  /** The registry, for ARRIVE IN's heights and leaving pushes. Without it the row stays off. */
  rows?: () => readonly BodyRow[];
}

export interface DvMapPanel {
  /** The autopilot's own line, once a frame; null while nothing flies. */
  setFlight: (flight: DvMapFlight | null) => void;
  /** The COURSE FIX and DEPARTURE rows' own answers, once a frame. */
  setCorrection: (correction: DvCorrectionState) => void;
  dispose: () => void;
}

function req<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`hudDvMap: no #${id} in the page`);
  return el as T;
}

function cell(cls: string, text: string): HTMLElement {
  const span = document.createElement('span');
  span.className = cls;
  span.textContent = text;
  return span;
}

/** A short-list row: the destination, its total and how long until it leaves. */
function buildRow(plan: TransferPlan): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'dv-row';
  wrap.dataset['dest'] = plan.destinationId;
  const head = document.createElement('button');
  head.className = 'dv-row-head';
  head.type = 'button';
  head.dataset['dest'] = plan.destinationId;
  head.append(cell('dv-row-name', ''), cell('dv-row-total', ''), cell('dv-row-window', ''));
  wrap.append(head);
  return wrap;
}

/** Repaint a row in place: text, class and tooltip on the same nodes, never
 *  new ones - a survey fill calls this on every chunk that lands, so the node a
 *  click is already travelling toward has to stay the one node throughout. */
function paintRow(wrap: HTMLElement, plan: TransferPlan, selected: boolean, working: boolean): void {
  wrap.className = selected ? 'dv-row dv-row-open' : 'dv-row';
  if (working) wrap.classList.add('dv-row-working');
  wrap.dataset['dest'] = plan.destinationId;
  const head = wrap.querySelector<HTMLButtonElement>('.dv-row-head');
  if (head === null) return;
  head.dataset['dest'] = plan.destinationId;
  head.setAttribute('aria-pressed', selected ? 'true' : 'false');
  if (ROW_TIPS) tip(head, planTip(plan, working));
  const [name, total, windowCell] = Array.from(head.children) as HTMLElement[];
  if (name) name.textContent = plainBodyName(plan.destinationName);
  if (total) {
    total.textContent = kms(plan.totalDvKmS);
    total.className = working ? 'dv-row-total dv-row-stale' : 'dv-row-total';
  }
  if (windowCell) windowCell.textContent = waitText(plan.windowWaitDays);
}

/**
 * Repaint the short list without tearing it down. A survey fill lands a chunk
 * at a time (`src/sim/dvMap.ts`'s `merge`), and a click already on its way to a
 * row must not land on a node the next chunk replaced. A destination already in
 * the list keeps its own nodes, repainted in place; only a destination new to
 * the list is built, and only one no longer in it is removed.
 */
function paintRowList(
  elList: HTMLElement, plans: readonly TransferPlan[], selectedId: string | null, working: ReadonlySet<string>,
): void {
  const existing = new Map<string, HTMLElement>();
  for (const child of Array.from(elList.children)) {
    const id = (child as HTMLElement).dataset['dest'];
    if (id !== undefined) existing.set(id, child as HTMLElement);
  }
  let after: ChildNode | null = null;
  for (const plan of plans) {
    let wrap = existing.get(plan.destinationId) ?? null;
    if (wrap === null) wrap = buildRow(plan);
    else existing.delete(plan.destinationId);
    paintRow(wrap, plan, plan.destinationId === selectedId, working.has(plan.destinationId));
    const wantNext: ChildNode | null = after === null ? elList.firstChild : after.nextSibling;
    if (wantNext !== wrap) elList.insertBefore(wrap, wantNext);
    after = wrap;
  }
  for (const stale of existing.values()) stale.remove();
}

export function createDvMapPanel(config: DvMapPanelConfig): DvMapPanel {
  const { map, root } = config;
  const control = config.flight ?? null;
  const elToggle = req<HTMLButtonElement>('dv-table-toggle');
  const elShrink = req<HTMLElement>('dv-map-shrink');
  const elListWrap = req<HTMLElement>('dv-list-wrap');
  const elList = req<HTMLElement>('dv-map-list');
  const elNote = req<HTMLElement>('dv-map-note');
  const elPick = req<HTMLElement>('dv-pick');
  const elPickName = req<HTMLElement>('dv-pick-name');
  const elPickTotal = req<HTMLElement>('dv-pick-total');
  const elOrbitRow = req<HTMLElement>('dv-orbit-row');
  const elOrbitLow = req<HTMLButtonElement>('dv-orbit-low');
  const elOrbitHigh = req<HTMLButtonElement>('dv-orbit-high');
  const elOrbitLeave = req<HTMLElement>('dv-orbit-leave');
  const elPickLeave = req<HTMLElement>('dv-pick-leave');
  const elPickArrive = req<HTMLElement>('dv-pick-arrive');
  const elPickWait = req<HTMLElement>('dv-pick-wait');
  const elPickTof = req<HTMLElement>('dv-pick-tof');
  const elPickNoGo = req<HTMLElement>('dv-pick-no-go');
  const elPickFigures = req<HTMLElement>('dv-pick-figures');
  const elPlanToggle = req<HTMLButtonElement>('dv-plan-toggle');
  const elPlanCount = req<HTMLElement>('dv-plan-count');
  const elPlan = req<HTMLElement>('dv-plan');
  const elPlanList = req<HTMLElement>('dv-plan-list');
  const elGoRow = req<HTMLElement>('dv-go-row');
  const elGo = req<HTMLButtonElement>('dv-go');
  const elCancel = req<HTMLButtonElement>('dv-cancel');
  const elSkip = req<HTMLButtonElement>('dv-warp-skip');
  const elFly = req<HTMLElement>('dv-fly');
  const elAutoWarp = req<HTMLButtonElement>('dv-autowarp');
  const elFlyDest = req<HTMLElement>('dv-fly-dest');
  const elFlyTripRow = req<HTMLElement>('dv-fly-trip-row');
  const elFlyTrip = req<HTMLElement>('dv-fly-trip');
  const elFlyPhase = req<HTMLElement>('dv-fly-phase');
  const elFlyNextStepRow = req<HTMLElement>('dv-fly-next-step-row');
  const elFlyNextStep = req<HTMLElement>('dv-fly-next-step');
  const elFlyDv = req<HTMLElement>('dv-fly-dv');
  const elFlyNext = req<HTMLElement>('dv-fly-next');
  const elFlyWhy = req<HTMLElement>('dv-fly-why');
  const elFixRow = req<HTMLElement>('dv-fix-row');
  const elFix = req<HTMLElement>('dv-fix');
  const elDepartRow = req<HTMLElement>('dv-depart-row');
  const elDepart = req<HTMLElement>('dv-depart');
  const elMoved = req<HTMLElement>('dv-moved');

  const glossary = new Map(DV_MAP_GLOSSARY.map((row) => [row.rowId, row.tip]));
  const glossaryTip = (rowId: string): string => (rowId === DROP_TIP_ROW ? '' : glossary.get(rowId) ?? '');

  const tripShare = (): number => config.thrustShare?.() ?? 1;
  const tripAccel = (): number => (config.accelMps2?.() ?? 0) * tripShare();

  const table = createDvTable({
    map,
    accelMps2: tripAccel,
    thrustShare: tripShare,
    glossaryTip,
    onOpenChange(open: boolean): void {
      elToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    },
  });

  /** The plan picked on the map, which is what GO would take. */
  let planned = false;
  /** Null while the autopilot has nothing; the last line it gave otherwise. */
  let flying: DvMapFlight | null = null;
  /** The text last written, so a per-frame call that changes nothing writes
   *  nothing: this panel redraws on a change and not on a tick. */
  let flyKey = '';
  /** The same for the COURSE FIX row, which is written every frame too. */
  let fixText = '';
  /** And for the DEPARTURE row, which counts down every frame it is on screen. */
  let departText = '';
  let lastState: DvMapState | null = null;
  /** Whether GO held the stick when the list and the picked trip were last drawn. */
  let bodyHeld = false;
  /** The FLIGHT PLAN expander, kept from one picked trip to the next. */
  let planOpen = false;
  /** The destination last scrolled into view, so a repaint from a countdown
   *  tick does not re-scroll the panel out from under the player's own scroll. */
  let scrolledToId: string | null = null;
  /** Whether that trip is still kept in view: the HUD fit pass shrinks the box a
   *  frame after a pick shows, so this holds from the pick until the player scrolls. */
  let revealOwed = false;

  if (TALL) {
    root.classList.add('dv-map-tall');
    delete elGo.dataset['hudKeep'];
  }
  if (NO_GUTTER) root.classList.add('dv-map-nogutter');
  for (const row of DV_MAP_GLOSSARY) {
    if (row.rowId === DROP_TIP_ROW) continue;
    const el = document.getElementById(row.rowId);
    if (el) tip(el, row.tip);
    document.querySelectorAll<HTMLElement>(`[data-glossary="${row.rowId}"]`).forEach((copy) => tip(copy, row.tip));
  }
  tip(elNote, NOTE_TIP);
  tip(elMoved, MOVED_TIP);
  tip(elPickNoGo, NO_GO_TIP);

  const holding = (): boolean => flying !== null && !flying.ended;

  /** The picked block brought into #dv-map-shrink's view by that box's own scroll
   *  alone: scrollIntoView also scrolls #app, which lifts the whole HUD and the canvas. */
  function revealPick(): void {
    if (elPick.getClientRects().length === 0) return;
    const box = elShrink.getBoundingClientRect();
    const pick = elPick.getBoundingClientRect();
    const above = box.top - pick.top;
    const below = pick.bottom - box.bottom;
    if (above > 0) elShrink.scrollTop -= above;
    else if (below > 0) elShrink.scrollTop += Math.min(below, -above);
  }

  /** ARRIVE IN, on a trip that brakes into an orbit: the pick, and the push to leave the orbit picked. */
  function paintOrbit(plan: TransferPlan): void {
    const orbit = lastState?.arrivalOrbit ?? 'high';
    elOrbitLow.classList.toggle('active', orbit === 'low');
    elOrbitHigh.classList.toggle('active', orbit === 'high');
    const rows = config.rows?.() ?? [];
    const body = rows.find((r) => r.id === plan.destinationId);
    const intoOrbit = plan.burns.some((b) => b.kind === 'capture' && b.frame === 'body-relative');
    elOrbitRow.classList.toggle('hidden', body === undefined || !intoOrbit);
    if (body === undefined || !intoOrbit) return;
    const radiusKm = body.radius_m / 1000 + arrivalAltitudeKm(body, rows, orbit);
    const accelKmS2 = tripAccel() / 1000;
    elOrbitLeave.textContent = `${leaveKmS(body, radiusKm, accelKmS2).toFixed(1)} km/s to leave`;
    tip(elOrbitRow, orbitTip(body, rows, accelKmS2));
  }

  /** What the list, the picked trip and the COURSE FIX row show, which turns
   *  on whether GO holds the stick as well as on the map's own state. */
  function paintBody(): void {
    const held = holding() && !TALL;
    elListWrap.classList.toggle('hidden', held);
    elFixRow.classList.toggle('hidden', !TALL && (fixText === '' || fixText === NOTHING));
    // Nothing to count down to is a blank row, not a dash: the departure leaves
    // the screen at the burn, where the COURSE FIX row takes over. TALL keeps it
    // up regardless, the same as the COURSE FIX row beside it.
    elDepartRow.classList.toggle('hidden', !TALL && (departText === '' || departText === NOTHING));
    const plan = lastState?.selected ?? null;
    elPick.classList.toggle('hidden', plan === null || held);
    if (plan === null) { scrolledToId = null; revealOwed = false; return; }
    const share = tripShare();
    const accelMps2 = tripAccel();
    elPickName.textContent = plainBodyName(plan.destinationName);
    elPickTotal.textContent = kms(plan.totalDvKmS);
    paintOrbit(plan);
    const departure = plan.burns.find((burn) => burn.kind === 'departure');
    const arrival = plan.burns.find((burn) => burn.kind === 'capture');
    elPickLeave.textContent = departure === undefined ? NOTHING : burnFigure(departure, accelMps2);
    elPickArrive.textContent = arrival === undefined ? NOTHING : burnFigure(arrival, accelMps2);
    if (departure !== undefined) tip(elPickLeave, burnTip(departure, accelMps2, share));
    if (arrival !== undefined) tip(elPickArrive, burnTip(arrival, accelMps2, share));
    elPickWait.textContent = waitText(plan.windowWaitDays);
    elPickTof.textContent = days(plan.timeOfFlightDays);
    const steps = planRows(plan, accelMps2, share);
    const planShown = planOpen && steps.length > 0;
    elPlanToggle.classList.toggle('hidden', steps.length === 0);
    elPlanToggle.setAttribute('aria-expanded', planOpen ? 'true' : 'false');
    elPlanCount.textContent = planOpen ? 'HIDE' : `SHOW ${steps.length} STEPS`;
    elPlan.classList.toggle('hidden', !planShown);
    elListWrap.classList.toggle('hidden', held || planShown);
    elPickFigures.classList.toggle('hidden', planShown);
    paintPlan(elPlanList, steps, RAIL_PLAN_COLS);
    const noGo = control !== null && !goOffered(plan);
    elPickNoGo.textContent = noGo ? `Autopilot: ${plainBodyName(plan.destinationName)} is yours to fly` : '';
    elPickNoGo.classList.toggle('hidden', !noGo);
    // The trip list gives up height first (its own data-hud-shrink floor);
    // only once that is not enough does #dv-map-shrink itself scroll, and a
    // newly picked trip's own rows are what a player needs to see then.
    if (!held && plan.destinationId !== scrolledToId) {
      scrolledToId = plan.destinationId;
      revealOwed = true;
      revealPick();
    }
  }

  /** Go, Cancel and the autopilot's four lines. Cheap to call every frame. */
  function paintFlight(): void {
    const fly = flying;
    // The button's own face, which is what the key below watches: the seconds
    // behind it fall every frame and the words they render as do not.
    const skipFace = fly === null || fly.ended || fly.skipStep === null || fly.skipSeconds <= 0
      ? '' : `WARP TO NEXT - ${durationText(fly.skipSeconds)}`;
    const autoOn = control?.autoWarp.on() ?? false;
    const autoState = fly !== null && !fly.ended ? fly.autoWarp : autoOn ? 'driving' : 'off';
    const tripText = fly === null || fly.ended || fly.tripElapsedS === null ? ''
      : `${tripClockText(fly.tripElapsedS)}`
        + `${fly.tripPlannedS !== null && fly.tripPlannedS > 0 ? ` of ${tripClockText(fly.tripPlannedS)}` : ''}`;
    const key = control === null ? 'off'
      : fly === null ? `idle|${planned ? 1 : 0}|${autoState}`
        : `${fly.phase}|${fly.destinationName}|${Math.round(fly.dvToGoMps)}`
          + `|${Math.round(fly.nextEventS)}|${fly.reason ?? ''}`
          + `|${fly.ended ? 1 : 0}|${planned ? 1 : 0}`
          + `|${skipFace}|${fly.nextStepLabel}|${fly.skipping ? 1 : 0}|${autoState}|${tripText}`;
    if (key === flyKey) return;
    flyKey = key;
    if (control === null) {
      elGoRow.classList.add('hidden');
      elSkip.classList.add('hidden');
      elFly.classList.add('hidden');
      return;
    }
    elAutoWarp.textContent = autoState === 'off' ? 'AUTO-WARP OFF'
      : autoState === 'stood down' ? 'AUTO-WARP PAUSED' : 'AUTO-WARP ON';
    elAutoWarp.setAttribute('aria-pressed', autoOn ? 'true' : 'false');
    elFlyTripRow.classList.toggle('hidden', tripText === '');
    if (tripText !== '') elFlyTrip.textContent = tripText;
    // A control is on screen only while it can act: no step ahead, or one
    // already inside the near lead the skip stops short by, and the button is
    // gone (the day the first press leaves is still inside that lead's reach).
    elSkip.classList.toggle('hidden', skipFace === '');
    if (skipFace !== '') {
      elSkip.textContent = skipFace;
      elSkip.title = skipFace;
    }
    elSkip.disabled = fly !== null && fly.skipping;
    elFlyNextStepRow.classList.toggle('hidden', skipFace === '');
    if (skipFace !== '' && fly !== null) elFlyNextStep.textContent = fly.nextStepLabel;
    // The row appears when there is a trip to fly or one being flown, so an
    // empty map carries no button that would do nothing.
    const held = holding();
    elGoRow.classList.toggle('hidden', fly === null && !planned);
    elGo.classList.toggle('hidden', held || (fly !== null && !planned));
    elCancel.classList.toggle('hidden', !held);
    elFly.classList.toggle('hidden', fly === null);
    if (held !== bodyHeld) {
      bodyHeld = held;
      paintBody();
    }
    if (fly === null) return;
    elFlyDest.textContent = fly.destinationName;
    elFlyPhase.textContent = PHASE_WORDS[fly.phase];
    elFlyDv.textContent = dvText(fly.dvToGoMps);
    elFlyNext.textContent = durationText(fly.nextEventS);
    elFlyWhy.textContent = fly.reason ?? '';
    elFlyWhy.classList.toggle('hidden', fly.reason === null);
  }

  function paint(state: DvMapState): void {
    lastState = state;
    root.classList.toggle('hidden', !state.open);
    // GO waits out the survey re-pricing the trip for a new ARRIVE IN pick.
    planned = state.selected !== null && goOffered(state.selected) && !state.arrivalRepricing;
    paintFlight();
    const movedText = state.moved === null ? '' : movedRowText(state.moved);
    elMoved.textContent = movedText;
    elMoved.classList.toggle('hidden', movedText === '');
    table.paint(state);
    if (!state.open) return;

    const plans = state.survey?.plans ?? [];
    const working = new Set(state.working);
    if (ROW_REPLACE) {
      elList.replaceChildren();
      for (const plan of plans) {
        const wrap = buildRow(plan);
        paintRow(wrap, plan, plan.destinationId === state.selectedId, working.has(plan.destinationId));
        elList.append(wrap);
      }
    } else {
      paintRowList(elList, plans, state.selectedId, working);
    }
    elNote.textContent = state.reason ?? '';
    elNote.classList.toggle('hidden', state.reason === null);
    paintBody();
  }

  const onListClick = (ev: MouseEvent): void => {
    const target = ev.target;
    if (!(target instanceof Element)) return;
    const head = target.closest<HTMLElement>('.dv-row-head');
    if (!head) return;
    const id = head.dataset['dest'] ?? null;
    map.select(map.state().selectedId === id ? null : id);
    const planned = tripPlanned(map.state(), 'list');
    if (planned) track('trip_planned', planned);
  };
  const onToggle = (): void => table.setOpen(!table.isOpen());
  const onPlanToggle = (): void => {
    planOpen = !planOpen;
    paintBody();
  };
  const onPlayerScroll = (): void => { revealOwed = false; };
  const reflow = new ResizeObserver(() => { if (revealOwed) revealPick(); });
  const pickOrbit = (orbit: ArrivalOrbit): void => {
    saveArrivalOrbit(orbit);
    map.setArrivalOrbit(orbit);
  };
  const onOrbitLow = (): void => pickOrbit('low');
  const onOrbitHigh = (): void => pickOrbit('high');
  const onGo = (): void => control?.go();
  const onCancel = (): void => control?.cancel();
  const onSkip = (): void => {
    if (flying) track('warp_skip', { target_id: map.state().selectedId ?? 'none', skipped_days: Math.round(flying.skipSeconds / 8640) / 10 });
    control?.skip();
  };
  // PAUSED is on and stood down for the trip in hand: pressing it resumes rather than switching off.
  const onAutoWarp = (): void => {
    if (control === null) return;
    const paused = flying !== null && !flying.ended && flying.autoWarp === 'stood down';
    control.autoWarp.set(paused || !control.autoWarp.on());
    paintFlight();
  };

  elList.addEventListener('click', onListClick);
  elToggle.addEventListener('click', onToggle);
  elPlanToggle.addEventListener('click', onPlanToggle);
  elOrbitLow.addEventListener('click', onOrbitLow);
  elOrbitHigh.addEventListener('click', onOrbitHigh);
  elGo.addEventListener('click', onGo);
  elCancel.addEventListener('click', onCancel);
  elSkip.addEventListener('click', onSkip);
  elAutoWarp.addEventListener('click', onAutoWarp);
  elShrink.addEventListener('wheel', onPlayerScroll, { passive: true });
  elShrink.addEventListener('pointerdown', onPlayerScroll);
  elShrink.addEventListener('keydown', onPlayerScroll);
  reflow.observe(elShrink);
  reflow.observe(elListWrap);
  const unsubscribe = map.subscribe(paint);
  paintFlight();

  return {
    setFlight(flight: DvMapFlight | null): void {
      flying = flight;
      paintFlight();
      table.setFlight(flight);
    },
    setCorrection(correction: DvCorrectionState): void {
      const text = correctionRowText(correction);
      const depart = departureRowText(correction);
      if (text === fixText && depart === departText) return;
      if (text !== fixText) {
        fixText = text;
        elFix.textContent = text;
        table.setCorrection(text);
      }
      if (depart !== departText) {
        departText = depart;
        elDepart.textContent = depart;
      }
      paintBody();
    },
    dispose(): void {
      unsubscribe();
      table.dispose();
      elList.removeEventListener('click', onListClick);
      elToggle.removeEventListener('click', onToggle);
      elPlanToggle.removeEventListener('click', onPlanToggle);
      elOrbitLow.removeEventListener('click', onOrbitLow);
      elOrbitHigh.removeEventListener('click', onOrbitHigh);
      elGo.removeEventListener('click', onGo);
      elCancel.removeEventListener('click', onCancel);
      elSkip.removeEventListener('click', onSkip);
      elAutoWarp.removeEventListener('click', onAutoWarp);
      elShrink.removeEventListener('wheel', onPlayerScroll);
      elShrink.removeEventListener('pointerdown', onPlayerScroll);
      elShrink.removeEventListener('keydown', onPlayerScroll);
      reflow.disconnect();
      elList.replaceChildren();
      elPlanList.replaceChildren();
      elGoRow.classList.add('hidden');
      elSkip.classList.add('hidden');
      elFly.classList.add('hidden');
      elMoved.classList.add('hidden');
      elPick.classList.add('hidden');
    },
  };
}
